import { useEffect, useRef, useState } from "react";
import { t, type Locale } from "../i18n";
import {
  fetchPlaceDetails,
  placesAutocomplete,
  type PlaceAddress,
  type PlacePrediction,
} from "../places";

type AddressAutocompleteProps = {
  locale: Locale;
  onResolved: (address: PlaceAddress) => void;
};

export function AddressAutocomplete({ locale, onResolved }: AddressAutocompleteProps) {
  const [query, setQuery] = useState("");
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounce = useRef<number | null>(null);
  const generation = useRef(0);

  useEffect(() => {
    return () => {
      if (debounce.current != null) window.clearTimeout(debounce.current);
    };
  }, []);

  function onQueryChanged(value: string) {
    setQuery(value);
    if (debounce.current != null) window.clearTimeout(debounce.current);
    if (value.trim().length < 3) {
      setPredictions([]);
      setError(null);
      setSearching(false);
      return;
    }
    debounce.current = window.setTimeout(() => {
      void search(value);
    }, 300);
  }

  async function search(value: string) {
    const current = ++generation.current;
    setSearching(true);
    setError(null);
    try {
      const result = await placesAutocomplete(value, locale);
      if (current !== generation.current) return;
      setPredictions(result.predictions);
      setError(result.error ?? null);
    } catch {
      if (current !== generation.current) return;
      setPredictions([]);
      setError(t(locale, "rrAddressNotFound"));
    } finally {
      if (current === generation.current) setSearching(false);
    }
  }

  async function select(prediction: PlacePrediction) {
    setSearching(true);
    const result = await fetchPlaceDetails(prediction.placeId);
    if (result.address) {
      onResolved(result.address);
      setQuery(result.address.formattedAddress || prediction.description);
      setPredictions([]);
      setSearching(false);
      return;
    }
    setSearching(false);
    setError(result.error ?? t(locale, "rrAddressNotFound"));
  }

  const emptyMessage =
    error ??
    (query.trim().length >= 3 ? t(locale, "rrAddressNotFound") : t(locale, "rrAddressSearchHint"));

  return (
    <div className="address-autocomplete">
      <label>
        {t(locale, "rrAddressSearch")}
        <input
          value={query}
          onChange={(e) => onQueryChanged(e.target.value)}
          placeholder={t(locale, "rrAddressSearchHint")}
          autoComplete="off"
        />
      </label>
      {searching && <p className="muted">{t(locale, "loading")}</p>}
      {predictions.length > 0 ? (
        <ul className="address-suggestions">
          {predictions.map((prediction) => (
            <li key={prediction.placeId}>
              <button type="button" onClick={() => void select(prediction)}>
                {prediction.description}
              </button>
            </li>
          ))}
        </ul>
      ) : query.trim().length >= 3 && !searching ? (
        <p className="muted">{emptyMessage}</p>
      ) : null}
    </div>
  );
}
